import { DsmPageContainer } from '@/components';
import {
  PalmettoButton,
  PalmettoCheckbox,
  PalmettoChip,
  PalmettoIcon,
  PalmettoOptionGroup,
  PalmettoTextarea,
  PalmettoTag,
  PalmettoTextInput,
  PalmettoTooltip,
} from '@palmetto/dsm-components';
import { Box, toast } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useForm, Controller } from 'react-hook-form';
import { NavLink, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import { documentPullCategories, documentTitles, type DocumentPullCategory } from 'types';

import { useCreateDocumentPullMutation } from '../../services/documentPull';
import PageHeader from '../PageHeader';

import { ReportsTab } from './ReportsTab';

const documentTypes = documentPullCategories.map((d) => d.label);

export const MAX_LISTED_TYPES = 6;

// Categories that span multiple document types don't say so on their label — e.g. "Contract" also
// carries install contracts and change order amendments. Surface the members so an admin knows what a
// checkbox will actually export. Gate on distinct titles: proofOfLoad and proofOfLoadHVAC share the
// title "Proof of Load", and listing that would only repeat the checkbox's own label.
export const buildIncludedTypesSummary = ({ documentTypes: types }: DocumentPullCategory): string | undefined => {
  const titles = [...new Set(types.map((type) => documentTitles[type] ?? type))];
  if (titles.length <= 1) return undefined;

  const listed = titles.slice(0, MAX_LISTED_TYPES).join(', ');
  const remainder = titles.length - MAX_LISTED_TYPES;
  return `Includes: ${remainder > 0 ? `${listed}, +${remainder} more` : listed}`;
};

const getFileNameTokens = (
  isTrancheMode: boolean,
  trancheName?: string,
): Array<{ token: string; example: string; disabled?: boolean }> => [
  { token: '{AccountID}', example: '692f29bd08e7927e8f578ea8' },
  { token: '{IRSNumber}', example: 'TZ0A000103X2', disabled: true },
  { token: '{Current Date}', example: DateTime.utc().toISODate() ?? '' },
  { token: '{TrancheName}', example: trancheName ?? 'Tranche A', disabled: !isTrancheMode },
];

interface DocumentPullFormValues {
  reportName: string;
  accountIds: string;
  format: 'zip' | 'pdf';
  selectedDocumentTypes: string[];
  selectedTokens: string[];
}

const GenerateTab = () => {
  const [searchParams] = useSearchParams();
  const trancheName = searchParams.get('trancheName') ?? undefined;
  const isTrancheMode = Boolean(trancheName);

  const { control, handleSubmit, watch } = useForm<DocumentPullFormValues>({
    defaultValues: {
      reportName: '',
      accountIds: '',
      format: 'zip',
      selectedDocumentTypes: [],
      selectedTokens: [],
    },
  });
  const [createDocumentPull, { isLoading }] = useCreateDocumentPullMutation();
  const navigate = useNavigate();

  const accountIds = watch('accountIds');
  const fileNameTokens = getFileNameTokens(isTrancheMode, trancheName);

  const onSubmit = async (data: DocumentPullFormValues) => {
    const parsedAccountIds = data.accountIds
      .split(/[,\n]/)
      .map((id) => id.trim())
      .filter(Boolean);

    if (!isTrancheMode && parsedAccountIds.length === 0) return;

    const documentTypeKeys = [
      ...new Set(
        data.selectedDocumentTypes.flatMap(
          (label) => documentPullCategories.find((d) => d.label === label)?.documentTypes ?? [],
        ),
      ),
    ];

    const accountPayload = isTrancheMode ? { trancheName } : { accountIds: parsedAccountIds };

    try {
      const result = await createDocumentPull({
        ...accountPayload,
        format: data.format,
        reportName: data.reportName,
        selectedDocumentTypes: documentTypeKeys,
        fileNameTokens: data.selectedTokens,
      }).unwrap();
      const requestIds = result.requests.map((r) => r.id).join(', ');
      toast.success(`${result.requests.length} export(s) queued. Request IDs: ${requestIds}`);
      navigate('/admin/document-pull/reports');
    } catch {
      toast.error('Failed to create export request');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box childGap="0">
        <Box direction="row" padding="lg 0" borderWidth="0 0 xs 0" borderColor="separator">
          <Box style={{ minWidth: '230px' }} padding="0 lg 0 0">
            <p className="text-sm font-bold">Step 1</p>
            <p className="heading-200 text-text-secondary">Name Your Report</p>
          </Box>
          <Box flex="auto" padding="lg" radius="md">
            <Controller
              name="reportName"
              control={control}
              rules={{ required: 'Report name is required' }}
              render={({ field, fieldState }) => (
                <PalmettoTextInput
                  id="reportName"
                  label="Report name"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  validationMessage={
                    fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
                  }
                />
              )}
            />
          </Box>
        </Box>

        <Box direction="row" padding="lg 0" borderWidth="0 0 xs 0" borderColor="separator">
          <Box style={{ minWidth: '230px' }} padding="0 lg 0 0">
            <p className="text-sm font-bold">Step 2</p>
            <p className="heading-200 text-text-secondary">Select Documents</p>
          </Box>
          <Box flex="auto" padding="lg" radius="md">
            <Controller
              name="selectedDocumentTypes"
              control={control}
              rules={{ validate: (v) => v.length > 0 || 'Select at least one document type' }}
              render={({ field, fieldState }) => (
                <Box childGap="md">
                  <Box width="100%">
                    <PalmettoCheckbox
                      id="doc-type-select-all"
                      label="Select All"
                      checked={field.value.length === documentTypes.length}
                      onCheckedChange={() => {
                        field.onChange(field.value.length === documentTypes.length ? [] : [...documentTypes]);
                      }}
                    />
                  </Box>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {documentPullCategories.map((category) => {
                      const includedTypes = buildIncludedTypesSummary(category);
                      return (
                        <div
                          key={category.slug}
                          // `relative` keeps PalmettoCheckbox's absolutely positioned input from
                          // anchoring to <body> and scrolling the page to the top when clicked.
                          className="relative flex items-center gap-6"
                          title={category.programTypes.length > 0 ? category.programTypes.join(', ') : undefined}
                        >
                          <PalmettoCheckbox
                            id={`doc-type-${category.slug}`}
                            label={category.label}
                            checked={field.value.includes(category.label)}
                            onCheckedChange={() => {
                              const updated = field.value.includes(category.label)
                                ? field.value.filter((d) => d !== category.label)
                                : [...field.value, category.label];
                              field.onChange(updated);
                            }}
                          />
                          {includedTypes && (
                            <PalmettoTooltip content={includedTypes} position="top-center">
                              <button type="button" aria-label={`What ${category.label} includes`}>
                                <PalmettoIcon name="infoSquare" size="sm" className="text-text-secondary" />
                              </button>
                            </PalmettoTooltip>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {fieldState.error && <p className="text-danger text-sm">{fieldState.error.message}</p>}
                </Box>
              )}
            />
          </Box>
        </Box>

        <Box direction="row" padding="lg 0" borderWidth="0 0 xs 0" borderColor="separator">
          <Box style={{ minWidth: '230px' }} padding="0 lg 0 0">
            <p className="text-sm font-bold">Step 3</p>
            <p className="heading-200 text-text-secondary">Choose a Format</p>
          </Box>
          <Box flex="auto" padding="lg" radius="md">
            <Controller
              name="format"
              control={control}
              render={({ field }) => (
                <PalmettoOptionGroup
                  type="radio"
                  name="format"
                  value={field.value}
                  onChange={(event) => {
                    const val = event.target.value;
                    if (val === 'zip' || val === 'pdf') {
                      field.onChange(val);
                    }
                  }}
                  options={[
                    { value: 'pdf', label: 'Compile as one PDF' },
                    { value: 'zip', label: 'Separate files (zip)' },
                  ]}
                />
              )}
            />
          </Box>
        </Box>

        <Box direction="row" padding="lg 0" borderWidth="0 0 xs 0" borderColor="separator">
          <Box style={{ minWidth: '230px' }} padding="0 lg 0 0">
            <p className="text-sm font-bold">Step 4</p>
            <p className="heading-200 text-text-secondary">Select Accounts</p>
          </Box>
          <Box flex="auto" padding="lg" radius="md" childGap="xs">
            {isTrancheMode ? (
              <Box childGap="xs">
                <p className="text-sm text-text-secondary">Accounts will be loaded from tranche:</p>
                <PalmettoTag label={trancheName!} color="palmetto" size="sm" className="w-fit" />
              </Box>
            ) : (
              <>
                <Controller
                  name="accountIds"
                  control={control}
                  rules={{ required: 'Account numbers are required' }}
                  render={({ field, fieldState }) => (
                    <PalmettoTextarea
                      id="accountIds"
                      label="Account number(s)"
                      placeholder="Enter account numbers"
                      value={field.value}
                      onChange={(event) => field.onChange(event.target.value)}
                      onBlur={field.onBlur}
                      rows={5}
                      validationMessage={
                        fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
                      }
                    />
                  )}
                />
                <p className="text-xs text-text-secondary">
                  Separate accounts with a comma: &quot;,&quot; or keep each account number to a single line.
                </p>
              </>
            )}
          </Box>
        </Box>

        <Box direction="row" padding="lg 0" borderWidth="0 0 xs 0" borderColor="separator">
          <Box style={{ minWidth: '230px' }} padding="0 lg 0 0">
            <p className="text-sm font-bold">Step 5</p>
            <p className="heading-200 text-text-secondary">Define File Name</p>
          </Box>
          <Box flex="auto" padding="lg" radius="md" childGap="md">
            <p className="text-sm text-text-secondary">Click tokens to build a file name template.</p>
            <Controller
              name="selectedTokens"
              control={control}
              render={({ field }) => (
                <>
                  <Box direction="row" childGap="sm" style={{ flexWrap: 'wrap' }}>
                    {fileNameTokens.map(({ token, disabled }) => (
                      <PalmettoChip
                        type="button"
                        key={token}
                        isSelected={field.value.includes(token)}
                        disabled={disabled}
                        onSelectedChange={() => {
                          const updated = field.value.includes(token)
                            ? field.value.filter((t) => t !== token)
                            : [...field.value, token];
                          field.onChange(updated);
                        }}
                      >
                        {token}
                      </PalmettoChip>
                    ))}
                  </Box>
                  {field.value.length > 0 && (
                    <Box padding="sm" background="primary" radius="sm" margin="sm 0 0 0">
                      <Box as="p" fontSize="sm" fontWeight="bold" color="body-secondary">
                        Preview
                      </Box>
                      <Box as="p" fontSize="sm" color="body-primary">
                        {field.value.map((t) => fileNameTokens.find((ft) => ft.token === t)?.example ?? t).join('_')}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            />
          </Box>
        </Box>

        <Box padding="lg 0" direction="row" justifyContent="flex-end">
          <Box width="fit-content">
            <PalmettoButton
              type="submit"
              loading={isLoading}
              disabled={(!isTrancheMode && !accountIds.trim()) || isLoading}
            >
              Generate Report
            </PalmettoButton>
          </Box>
        </Box>
      </Box>
    </form>
  );
};

const basePath = '/admin/document-pull';

export const DocumentPull = () => {
  return (
    <DsmPageContainer title="Report Generation">
      <PageHeader title="Report Generation" />
      <Box direction="row" borderWidth="0 0 xs 0" borderColor="separator">
        <NavLink to={basePath} style={{ textDecoration: 'none', fontWeight: '500' }} end>
          {({ isActive }) => (
            <Box
              as="span"
              padding="md"
              className={isActive ? 'tab-active' : ''}
              hover={{ color: 'primary' }}
              style={{ transitionDuration: '0s' }}
            >
              Generate
            </Box>
          )}
        </NavLink>
        <NavLink to={`${basePath}/reports`} style={{ textDecoration: 'none', fontWeight: '500' }}>
          {({ isActive }) => (
            <Box
              as="span"
              padding="md"
              className={isActive ? 'tab-active' : ''}
              hover={{ color: 'primary' }}
              style={{ transitionDuration: '0s' }}
            >
              Reports
            </Box>
          )}
        </NavLink>
      </Box>
      <Routes>
        <Route path="/" element={<GenerateTab />} />
        <Route path="/reports" element={<ReportsTab />} />
      </Routes>
    </DsmPageContainer>
  );
};
